import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import type { Court, CourtHireRate, Currency } from '@court-sync/shared';
import { getCurrencySymbol } from '@/utils/currency';
import {
  Button,
  Drawer,
  Field,
  FieldError,
  FieldLabel,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@/components/ui';
import { courtHireService } from '@/services/courtHire';
import { DrawerTourHelp } from '@/components/tour/DrawerTourHelp';
import { COURT_HIRE_RATE_CREATE_TOUR_STEP_IDS } from '@/lib/tour-constants';

const RATE_FORM_ID = 'court-hire-rate-form';
const ALL_COURTS_VALUE = '__all__';

export interface CourtHireRateFormValues {
  name: string;
  durationMinutes: string;
  price: string;
  courtId: string;
  isActive: boolean;
  membersOnly: boolean;
}

interface CourtHireRateDrawerProps {
  clubId: string;
  currency: string;
  courts: Court[];
  rate: CourtHireRate | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  onCreatePanelOpen?: () => void;
  showCreateTourHelp?: boolean;
  showBackdrop?: boolean;
}

function toFormValues(rate: CourtHireRate | null): CourtHireRateFormValues {
  if (!rate) {
    return {
      name: '',
      durationMinutes: '60',
      price: '',
      courtId: ALL_COURTS_VALUE,
      isActive: true,
      membersOnly: false,
    };
  }

  return {
    name: rate.name,
    durationMinutes: String(rate.durationMinutes),
    price: (rate.priceCents / 100).toFixed(2),
    courtId: rate.courtId ?? ALL_COURTS_VALUE,
    isActive: rate.isActive,
    membersOnly: rate.membersOnly,
  };
}

export function resolveCourtHireRateCurrency(
  rate: CourtHireRate | null,
  createCurrency: string
): string {
  return rate?.currency ?? createCurrency;
}

export function CourtHireRateDrawer({
  clubId,
  currency,
  courts,
  rate,
  isOpen,
  onClose,
  onSaved,
  onCreatePanelOpen,
  showCreateTourHelp = false,
  showBackdrop = true,
}: CourtHireRateDrawerProps) {
  const hireableCourts = courts.filter((court) => court.availableForHire);
  const isEditing = rate != null;
  const effectiveCurrency = resolveCourtHireRateCurrency(rate, currency);
  const currencySymbol = getCurrencySymbol(effectiveCurrency as Currency);

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CourtHireRateFormValues>({
    defaultValues: toFormValues(rate),
  });

  useEffect(() => {
    if (isOpen) {
      reset(toFormValues(rate));
    }
  }, [isOpen, rate, reset]);

  const onSubmit = async (values: CourtHireRateFormValues) => {
    const durationMinutes = Number.parseInt(values.durationMinutes, 10);
    const price = Number.parseFloat(values.price);
    const priceCents = Math.round(price * 100);
    const courtId = values.courtId === ALL_COURTS_VALUE ? null : values.courtId;

    if (!values.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!Number.isFinite(durationMinutes) || durationMinutes < 15) {
      toast.error('Duration must be at least 15 minutes');
      return;
    }
    if (!Number.isFinite(price) || price < 0) {
      toast.error('Enter a valid price');
      return;
    }

    const payload = {
      name: values.name.trim(),
      durationMinutes,
      priceCents,
      currency: effectiveCurrency,
      courtId,
      isActive: values.isActive,
      membersOnly: values.membersOnly,
    };

    const res = isEditing
      ? await courtHireService.updateRate(clubId, rate.id, payload)
      : await courtHireService.createRate(clubId, payload);

    if (res.success) {
      toast.success(isEditing ? 'Rate updated' : 'Rate created');
      onSaved();
      onClose();
    } else {
      toast.error(res.error || (isEditing ? 'Failed to update rate' : 'Failed to create rate'));
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      showBackdrop={showBackdrop}
      title={isEditing ? 'Edit hire rate' : 'Create hire rate'}
      description="Set the session length and price. Leave court as all courts to apply club-wide, or pick one court."
      width="md"
      panelId={!isEditing ? COURT_HIRE_RATE_CREATE_TOUR_STEP_IDS.DRAWER : undefined}
      onPanelOpen={!isEditing ? onCreatePanelOpen : undefined}
      headerActions={
        showCreateTourHelp ? (
          <DrawerTourHelp
            tourKey="club-court-hire-rate-create"
            suggestion="New to hire rates? Tour this form before you save."
            buttonLabel="Tour this form"
          />
        ) : undefined
      }
      footer={
        <div
          id={!isEditing ? COURT_HIRE_RATE_CREATE_TOUR_STEP_IDS.SUBMIT : undefined}
          className="flex justify-end gap-2"
        >
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form={RATE_FORM_ID} isLoading={isSubmitting}>
            {isEditing ? 'Save rate' : 'Create rate'}
          </Button>
        </div>
      }
    >
      <form id={RATE_FORM_ID} onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <div id={!isEditing ? COURT_HIRE_RATE_CREATE_TOUR_STEP_IDS.DETAILS : undefined}>
        <Field data-invalid={!!errors.name?.message}>
          <FieldLabel htmlFor="hire-rate-name">Name</FieldLabel>
          <Input
            id="hire-rate-name"
            placeholder="e.g. 60 min off-peak"
            aria-invalid={!!errors.name?.message}
            {...register('name', { required: 'Name is required' })}
          />
          <FieldError>{errors.name?.message}</FieldError>
        </Field>

        <Field data-invalid={!!errors.durationMinutes?.message}>
          <FieldLabel htmlFor="hire-rate-duration">Duration (minutes)</FieldLabel>
          <Input
            id="hire-rate-duration"
            type="number"
            min={15}
            step={15}
            aria-invalid={!!errors.durationMinutes?.message}
            {...register('durationMinutes', { required: 'Duration is required' })}
          />
          <FieldError>{errors.durationMinutes?.message}</FieldError>
        </Field>

        <Field data-invalid={!!errors.price?.message}>
          <FieldLabel htmlFor="hire-rate-price">Price ({currencySymbol})</FieldLabel>
          <Input
            id="hire-rate-price"
            type="number"
            min={0}
            step={0.01}
            placeholder="0.00"
            aria-invalid={!!errors.price?.message}
            {...register('price', { required: 'Price is required' })}
          />
          <FieldError>{errors.price?.message}</FieldError>
        </Field>

        <Field>
          <FieldLabel htmlFor="hire-rate-court">Court</FieldLabel>
          <Controller
            name="courtId"
            control={control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger id="hire-rate-court">
                  <SelectValue placeholder="All courts" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_COURTS_VALUE}>All courts</SelectItem>
                  {hireableCourts.map((court) => (
                    <SelectItem key={court.id} value={court.id}>
                      {court.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {hireableCourts.length === 0 ? (
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              No courts are marked available for hire yet. Enable hire on a court in Courts
              management, or save a club-wide rate above.
            </p>
          ) : null}
        </Field>
        </div>

        <div className="flex items-start gap-3">
          <Controller
            name="membersOnly"
            control={control}
            render={({ field }) => (
              <Switch
                id="hire-rate-members-only"
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            )}
          />
          <label htmlFor="hire-rate-members-only" className="cursor-pointer text-sm">
            <span className="font-medium text-gray-900 dark:text-white">Members only</span>
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              Only members with an active paid membership can see and book this rate.
            </p>
          </label>
        </div>

        <div className="flex items-start gap-3">
          <Controller
            name="isActive"
            control={control}
            render={({ field }) => (
              <Switch
                id="hire-rate-active"
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            )}
          />
          <label htmlFor="hire-rate-active" className="cursor-pointer text-sm">
            <span className="font-medium text-gray-900 dark:text-white">Active</span>
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              Inactive rates are hidden from public court hire booking.
            </p>
          </label>
        </div>
      </form>
    </Drawer>
  );
}
